import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, flatMap, tap, map } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import { PermissionService } from '../services/permission.service';

@Component({
    styles: [`
        .scroll-container {
            max-height: 250px;
            min-height: 250px;
            height: 250px;
            overflow-y: auto;
        }
    `],
    template: `
        <div mat-dialog-title>{{title}}</div>
        <div mat-dialog-content>
            <div fxLayout="column" style="width: 500px;">
                <mat-form-field>
                    <input matInput [formControl]="searchControl" placeholder="Search Members">
                </mat-form-field>
                <div class="scroll-container" fxFlex infiniteScroll [infiniteScrollThrottle]="300" [scrollWindow]="false" (scrolled)="nextPage()">
                <table mat-table 
                        [dataSource]="users"
                        matSort 
                        [matSortActive]="searchParams.value.sort" 
                        [matSortDirection]="searchParams.value.dir"
                        [matSortDisableClear]="true"
                        (matSortChange)="onSort($event)" 
                        fxFlex 
                        width="100%;">
                        <ng-container matColumnDef="firstName">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="firstNameLower">First Name</th>
                            <td mat-cell *matCellDef="let row">{{row.firstName}}</td>
                        </ng-container>
                        <ng-container matColumnDef="lastName">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="lastNameLower">Last Name</th>
                            <td mat-cell *matCellDef="let row">{{row.lastName}}</td>
                        </ng-container>
                        <ng-container matColumnDef="emailAddress">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="emailAddress">Email</th>
                            <td mat-cell *matCellDef="let row">{{row.emailAddress}}</td>
                        </ng-container>
                        <ng-container matColumnDef="action">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let row">
                                <button mat-button color="primary" [mat-dialog-close]="row">Select</button>
                            </td>
                        </ng-container>  
                        <tr mat-header-row *matHeaderRowDef="['firstName', 'lastName', 'emailAddress', 'action']"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['firstName', 'lastName', 'emailAddress', 'action'];"></tr>
                    </table>
                </div>
            </div>
        </div>
        <div mat-dialog-actions>
            <button mat-button mat-dialog-close>Cancel</button>
        </div>
    `
})
export class SelectUserDialogComponent implements OnDestroy {
    private cleanupTime = new Subject<void>();
    searchParams: BehaviorSubject<any> = new BehaviorSubject({ page: 0 });
    users = new BehaviorSubject([]);
    title: string;
    searchControl = new FormControl();
    isLast: any;

    constructor(@Inject(MAT_DIALOG_DATA) public data: any,
                permissionService: PermissionService, 
                userService: UserService) {
        this.title = this.data.title || 'Continue?';

        this.searchParams
        .pipe(
            flatMap(params => userService.searchUsers(params)),
            tap(res => this.isLast = res.last),
            map(res => res.content),
            map(users => users.filter(user => permissionService.userHasPermission(user, { action: 'CREATE_STREAM'}))),
            takeUntil(this.cleanupTime)
        ).subscribe(users => this.users.next(this.users.value.concat(users)));

        this.searchControl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => this.users.next([])),
            takeUntil(this.cleanupTime)
        ).subscribe(searchTerm => this.searchParams.next(Object.assign({}, this.searchParams.value, {searchTerm, page: 0})));
    }

    nextPage() {
        if (!this.isLast) {
            this.searchParams.next(Object.assign({}, this.searchParams.value, { page: ++this.searchParams.value.page}));
        }
    }

    onSort($event) {
        this.users.next([]);
        this.searchParams.next(Object.assign({}, this.searchParams.value, { page: 0, sort: $event.active, sortDirection: $event.direction}));
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        this.searchParams.complete();
        this.users.complete();
    }
};